import { useMemo, useState, useCallback, useEffect } from 'react'
import * as React from 'react'
import { Button } from 'blaze-ui/Button'
import { useModals } from 'almanac-ui/Modal'
import { FontPairingsModal } from 'components/FontPairingsModal'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useFontPairings } from 'hooks/queries/fontPairings'
import { useFonts } from 'hooks/queries/workspaces/useFonts'
import { deleteFont } from 'hooks/queries/workspaces/useDeleteFont'
import { useUpsertTextStyle } from 'hooks/queries/workspaces/brandVisualKits/useUpsertTextStyle'
import { useUpdateBrandVisualKit } from 'hooks/queries/workspaces/useUpdateBrandVisualKit'
import { FontRole, FontStyle } from 'types'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import Styles from './BrandVisualStyleFonts.module.scss'
import SharedStyles from '../BrandVisualStyleSelection/BrandVisualStyleSelection.module.scss'
import { StyleGenerated } from '../BrandVisualStyleSelection/GenerationStage'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { FontPairingCard, FontPairingCardSkeleton } from 'components/FontPairingCard/FontPairingCard'
import { FontPairing } from 'hooks/queries/fontPairings'
import { VisualStylePreview } from 'components/VisualStylePreview/VisualStylePreview'
import { hexToRgb } from 'utils/colorHelpers'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { compact } from 'lodash'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Font01 } from 'blaze-ui/icons/24'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'
import { useFontPreloading } from 'hooks/useFontPreloading'
import { TopBar } from '../TopBar/TopBar'
import { useOnboardingStrategiesEnabled } from 'flows/virtualMarketerOnboarding/steps'

export const isTooContrasty = (hexColor: string): boolean => {
  const rgb = hexToRgb(hexColor)
  if (!rgb) return false

  // Calculate relative luminance using sRGB color space
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
  return luminance > 0.9 || luminance < 0.1
}

export interface BrandVisualStyleFontsProps {
  onNext: (selectedFontPairingId: number | null) => void
  onBack: () => void
  onProvideBackHandler?: (handler: (() => void) | null) => void
  brandKitId?: number
  initialFontPairings: FontPairing[]
  selectedStyle: StyleGenerated
  initialFontPairingId: number | null
  highlightStep?: string
}

export const BrandVisualStyleFonts = ({
  onNext,
  onBack,
  onProvideBackHandler,
  brandKitId,
  initialFontPairings,
  selectedStyle,
  initialFontPairingId,
  highlightStep = 'yourBrand',
}: BrandVisualStyleFontsProps) => {
  const { openModal } = useModals()
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()
  const queryClient = useQueryClient()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const [fontPairings, setFontPairings] = useState<FontPairing[]>(initialFontPairings)
  const [selectedFontPairingId, setSelectedFontPairingId] = useState<number | null>(
    initialFontPairingId || initialFontPairings[0]?.id,
  )
  const [isSaving, setIsSaving] = useState(false)
  const [showMobileFontSelector, setShowMobileFontSelector] = useState(false)
  const [fontSelectorSelectedId, setFontSelectorSelectedId] = useState<number | null>(selectedFontPairingId)

  const selectedVisualStyleId = selectedStyle.id

  // Fetch brand kit to get brand_visual_kit
  const { data: brandKitData } = useBrandKit(brandKitId, { enabled: !!brandKitId })
  const brandVisualKit = brandKitData?.brand_kit?.brand_visual_kit
  const brandVisualKitId = brandVisualKit?.id

  // Get the primary brand color from the brand kit's color palette
  const brandColors = brandVisualKit?.primary_color_palette?.colors
  const brandColor = brandColors?.find((color) => !isTooContrasty(color.hex))?.hex || brandColors?.[0]?.hex

  // Fetch font pairings to get font configs
  const { data: fontPairingsData, isLoading: isLoadingFontPairings } = useFontPairings()

  const selectedFontPairing = fontPairingsData?.find((fp) => fp.id === selectedFontPairingId)

  // Fetch custom fonts to detect if user has uploaded any
  const { data: fontsData, isLoading: isLoadingCustomFonts } = useFonts()
  const customFonts = useMemo(() => fontsData?.fonts ?? [], [fontsData?.fonts])
  const hasCustomFonts = customFonts.length > 0

  const isLoading = isLoadingFontPairings || isLoadingCustomFonts

  // Delete all custom fonts (used when user selects a font pairing instead)
  const deleteAllCustomFonts = async () => {
    if (customFonts.length === 0) return
    await Promise.all(customFonts.map((font) => deleteFont({ workspaceId: currentWorkspaceId, id: font.id })))
    queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'fonts', 'list'])
  }

  const handleBack = useCallback(() => {
    if (showMobileFontSelector) {
      setShowMobileFontSelector(false)
    } else {
      onBack()
    }
  }, [showMobileFontSelector, onBack])

  useEffect(() => {
    if (!onProvideBackHandler || !isMobile) return
    onProvideBackHandler(handleBack)
  }, [handleBack, onProvideBackHandler, isMobile])

  // Mutations for saving
  const { mutateAsync: updateBrandVisualKit } = useUpdateBrandVisualKit({
    id: brandVisualKitId as number,
  })
  const { mutateAsync: upsertTitleStyle } = useUpsertTextStyle({
    brandVisualKitId: brandVisualKitId as number,
    fontRole: FontRole.Title,
  })
  const { mutateAsync: upsertBodyStyle } = useUpsertTextStyle({
    brandVisualKitId: brandVisualKitId as number,
    fontRole: FontRole.Body,
  })

  const handleOpenFontPairingsModal = () => {
    if (isMobile) {
      setFontSelectorSelectedId(selectedFontPairingId)
      setShowMobileFontSelector(true)
    } else {
      openModal(FontPairingsModal, {
        selectedFontPairingId,
        title: 'Font Styles',
        onConfirm: async (newFontPairingId: number) => {
          await deleteAllCustomFonts()
          setSelectedFontPairingId(newFontPairingId)

          if (selectedFontPairingId !== null && !fontPairings.some((fp) => fp.id === newFontPairingId)) {
            const newFontPairing = fontPairingsData?.find((fp) => fp.id === newFontPairingId)

            if (newFontPairing) {
              setFontPairings([newFontPairing, ...fontPairings.slice(0, 2)])
            }
          }
        },
      })
    }
  }

  const handleNext = async () => {
    setIsSaving(true)
    const tasks: Promise<unknown>[] = []

    // Save visual style - pass the full brand visual kit record with the new visual_style_id
    if (selectedVisualStyleId && brandVisualKit) {
      tasks.push(updateBrandVisualKit({ ...brandVisualKit, visual_style_id: selectedVisualStyleId }))
    }

    // Save font pairing as text styles (same approach as BrandKitConfirm)
    if (selectedFontPairingId && fontPairingsData) {
      const selectedPairing = fontPairingsData.find((fp) => fp.id === selectedFontPairingId)
      if (selectedPairing) {
        if (selectedPairing.header_font_config) {
          const headerConfig = selectedPairing.header_font_config
          tasks.push(
            upsertTitleStyle({
              style: {
                font_family: headerConfig.family,
                font_weight: headerConfig.weight as FontStyle['font_weight'],
                letter_spacing: headerConfig.letter_spacing,
                line_height: headerConfig.line_height,
                italic: headerConfig.italic,
                uppercase: headerConfig.uppercase,
              },
              name: headerConfig.family,
            }),
          )
        }
        if (selectedPairing.body_font_config) {
          const bodyConfig = selectedPairing.body_font_config
          tasks.push(
            upsertBodyStyle({
              style: {
                font_family: bodyConfig.family,
                font_weight: bodyConfig.weight as FontStyle['font_weight'],
                letter_spacing: bodyConfig.letter_spacing,
                line_height: bodyConfig.line_height,
                italic: bodyConfig.italic,
                uppercase: bodyConfig.uppercase,
              },
              name: bodyConfig.family,
            }),
          )
        }
      }
    }

    if (tasks.length > 0) {
      await Promise.all(tasks).catch(() => {
        setIsSaving(false)
        return
      })
      queryClient.refetchQueries(['workspaces', currentWorkspaceId, 'brandKits'])
    }

    setIsSaving(false)
    onNext(selectedFontPairingId)
  }

  const displayedFontPairings = useMemo(() => {
    if (fontPairings.some((fp) => fp.id === selectedFontPairingId)) return fontPairings

    return compact([selectedFontPairing, ...fontPairings.filter((fp) => fp.id !== selectedFontPairingId)]).slice(0, 3)
  }, [selectedFontPairing, fontPairings, selectedFontPairingId])

  const headerFontStyle = useMemo(() => {
    if (hasCustomFonts && customFonts[0]) {
      return {
        fontFamily: `"${customFonts[0].font_family}", sans-serif`,
        fontWeight: 400,
        fontStyle: 'normal' as const,
      }
    }

    const config = selectedFontPairing?.header_font_config
    if (!config) return {}

    return {
      fontFamily: `"${config.family}", sans-serif`,
      fontWeight: config.weight,
      fontStyle: config.italic ? ('italic' as const) : ('normal' as const),
      letterSpacing: `${config.letter_spacing / 1000}em`,
    }
  }, [hasCustomFonts, customFonts, selectedFontPairing?.header_font_config])

  const headerFontFamily = customFonts[0]?.font_family ?? selectedFontPairing?.header_font_config?.family

  // Preload fonts for mobile (needed because mobile renders live text in cards)
  useFontPreloading(fontPairingsData, customFonts)

  if (isMobile && showMobileFontSelector) {
    const selectedFontPairing = fontPairingsData?.find((fp) => fp.id === fontSelectorSelectedId)

    const handleFontSelectorConfirm = async () => {
      if (fontSelectorSelectedId !== null) {
        await deleteAllCustomFonts()
        setSelectedFontPairingId(fontSelectorSelectedId)

        if (!fontPairings.some((fp) => fp.id === fontSelectorSelectedId)) {
          const newFontPairing = fontPairingsData?.find((fp) => fp.id === fontSelectorSelectedId)
          if (newFontPairing) {
            setFontPairings([newFontPairing, ...fontPairings.slice(0, 2)])
          }
        }

        setShowMobileFontSelector(false)
      }
    }

    return (
      <div className={Styles.mobileFontSelectorScreen} data-visual-style-page="true">
        <GoogleFontLoader family={headerFontFamily} />
        <BlazeFontLoader name={headerFontFamily} />
        <div className={Styles.mobileFontSelectorContent}>
          <div className={Styles.mobileFontSelectorGrid}>
            {fontPairingsData?.map((fontPairing) => (
              <div
                key={fontPairing.id}
                className={cx(Styles.mobileFontSelectorCard, {
                  [Styles.selected]: fontSelectorSelectedId === fontPairing.id,
                })}
                onClick={() => setFontSelectorSelectedId(fontPairing.id)}
              >
                <p
                  className={Styles.headerFont}
                  style={{
                    fontFamily: `"${fontPairing.header_font_config?.family}", sans-serif`,
                    fontWeight: fontPairing.header_font_config?.weight,
                    textTransform: fontPairing.header_font_config?.uppercase ? 'uppercase' : 'none',
                  }}
                >
                  {fontPairing.name?.split(' + ')[0] || fontPairing.header_font_config?.family}
                </p>
                <p
                  className={Styles.bodyFont}
                  style={{
                    fontFamily: `"${fontPairing.body_font_config?.family}", sans-serif`,
                    fontWeight: fontPairing.body_font_config?.weight,
                  }}
                >
                  {fontPairing.name?.split(' + ')[1] || fontPairing.body_font_config?.family}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className={Styles.mobileFontSelectorBottomCta}>
          <div className={Styles.mobileFontSelectorName}>
            <Heading level={2} color="var(--light-100)">
              {selectedFontPairing?.name || ''}
            </Heading>
          </div>
          <Button
            variant="secondary"
            size="lg"
            onPress={handleFontSelectorConfirm}
            isDisabled={fontSelectorSelectedId === null}
            className={Styles.mobileFontSelectorUseButton}
          >
            Use this font
          </Button>
        </div>
      </div>
    )
  }

  if (isMobile) {
    return (
      <MultiStepStepLayout>
        <GoogleFontLoader family={headerFontFamily} />
        <BlazeFontLoader name={headerFontFamily} />

        <div className={Styles.mobileContainer} data-visual-style-page="true">
          {isOnboardingStrategiesEnabled && (
            <div className={Styles.mobileTopBar}>
              <Text color="var(--light-100)">Choose a brand font</Text>
            </div>
          )}
          <div className={Styles.mobileHeroBackground}>
            <img
              src={selectedStyle.styleUrl || selectedStyle.cover_image_url}
              alt=""
              className={Styles.mobileHeroImage}
            />
            <div className={Styles.mobileHeroGradient} />
          </div>

          <div className={Styles.mobileContentOverlay}>
            <div className={Styles.sampleTextBox}>
              <span style={headerFontStyle}>Get access to loyalty</span>
            </div>
            <div className={Styles.sampleTextBox}>
              <span style={headerFontStyle}>discounts and savings</span>
            </div>
          </div>

          <div className={Styles.mobileFontSection}>
            <div className={Styles.mobileFontCardsContainer}>
              {!isLoadingFontPairings &&
                !hasCustomFonts &&
                displayedFontPairings.map((fontPairing: FontPairing) => (
                  <div
                    key={fontPairing.id}
                    className={cx(Styles.mobileFontCard, {
                      [Styles.selected]: selectedFontPairingId === fontPairing.id,
                    })}
                    onClick={() => setSelectedFontPairingId(fontPairing.id)}
                  >
                    <p
                      className={Styles.headerFont}
                      style={{
                        fontFamily: `"${fontPairing.header_font_config?.family}", sans-serif`,
                        fontWeight: fontPairing.header_font_config?.weight,
                        textTransform: fontPairing.header_font_config?.uppercase ? 'uppercase' : 'none',
                      }}
                    >
                      {fontPairing.name?.split(' + ')[0] || fontPairing.header_font_config?.family}
                    </p>
                    <p
                      className={Styles.bodyFont}
                      style={{
                        fontFamily: `"${fontPairing.body_font_config?.family}", sans-serif`,
                        fontWeight: fontPairing.body_font_config?.weight,
                      }}
                    >
                      {fontPairing.name?.split(' + ')[1] || fontPairing.body_font_config?.family}
                    </p>
                  </div>
                ))}

              <div className={Styles.mobileBrowseAllCard} onClick={handleOpenFontPairingsModal}>
                <Font01 size={24} color="var(--light-100)" />
                <Text variant="label" color="var(--light-100)">
                  Browse All
                  <br />
                  Fonts
                </Text>
              </div>
            </div>

            <Button
              size="lg"
              variant="secondary"
              onPress={handleNext}
              isDisabled={isSaving || isLoading}
              className={Styles.mobileUseButton}
            >
              {isSaving ? (
                <>
                  Saving
                  <LoadingEllipses />
                </>
              ) : (
                'Use this font'
              )}
            </Button>
          </div>
        </div>
      </MultiStepStepLayout>
    )
  }

  const nextButtonText = isOnboardingStrategiesEnabled ? 'Next, review preferences' : 'Looks good'

  return (
    <div className={cx(SharedStyles.selectImageStep, Styles.container)}>
      <div className={cx(SharedStyles.leftSide, Styles.leftSide)}>
        <TopBar highlightStep={highlightStep as React.ComponentProps<typeof TopBar>['highlightStep']} leftSide />
        <div className={isOnboardingStrategiesEnabled ? undefined : SharedStyles.title}>
          <Heading level={1} color="var(--dark-80)">
            {isOnboardingStrategiesEnabled ? 'Choose your typeface' : 'Select a brand font'}
          </Heading>
          {!isOnboardingStrategiesEnabled && (
            <Text variant="primary" color="var(--dark-40)">
              Based on your brand, we recommend:
            </Text>
          )}
        </div>

        <div className={cx(SharedStyles.content, Styles.content)}>
          <div className={Styles.fontPairings}>
            {isLoadingFontPairings && [1, 2, 3].map((i) => <FontPairingCardSkeleton key={i} />)}
            {!isLoadingFontPairings && hasCustomFonts && (
              <div className={Styles.customFontCard}>
                <Text variant="primary" color="var(--dark-80)">
                  Custom Font Uploaded
                </Text>
                <Text variant="label" color="var(--dark-40)">
                  Your uploaded fonts will be used for social posts
                </Text>
              </div>
            )}
            {!isLoadingFontPairings &&
              !hasCustomFonts &&
              displayedFontPairings.map((fontPairing: FontPairing) => (
                <FontPairingCard
                  key={fontPairing.id}
                  fontPairing={fontPairing}
                  isSelected={selectedFontPairingId === fontPairing.id}
                  onSelect={() => setSelectedFontPairingId(fontPairing.id)}
                  className={Styles.fontPairingCard}
                />
              ))}
          </div>
          <div className={Styles.actions}>
            <Button size="lg" variant="secondary" onPress={handleOpenFontPairingsModal}>
              Browse all fonts
            </Button>
          </div>
        </div>
        <div className={SharedStyles.footer}>
          <Button variant="tertiary" size="lg" onPress={onBack} isDisabled={isSaving}>
            Back
          </Button>
          <Button size="lg" onPress={handleNext} isDisabled={isSaving || isLoading}>
            {isSaving ? (
              <>
                Saving
                <LoadingEllipses />
              </>
            ) : (
              nextButtonText
            )}
          </Button>
        </div>
      </div>
      <div
        className={cx(SharedStyles.transformedImageContainer, Styles.transformedImageContainer, {
          [Styles.loading]: false,
        })}
      >
        <VisualStylePreview
          fontPairingId={selectedFontPairingId}
          brandColor={brandColor}
          styleGenerated={selectedStyle}
          fontPairings={fontPairings}
          firstCustomFont={customFonts[0]}
          accountName={brandKitData?.brand_kit?.brand_profile?.name}
          avatarUrl={brandKitData?.brand_kit?.brand_visual_kit?.primary_logo?.url}
          brandKitId={brandKitId}
          brandVoiceId={brandKitData?.brand_kit?.brand_voice?.id}
          brandKitName={brandKitData?.brand_kit?.name}
        />
      </div>
    </div>
  )
}
